// operationsHelpers.js
// Small, dependency-free helpers used by the Operations Dashboard feature.
// No external date library is required.

/**
 * Format a JS Date as YYYY-MM-DD using LOCAL time (not UTC), so the date
 * shown to the user always matches the date sent to the backend.
 */
export const formatDateYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Parse a YYYY-MM-DD string into a local JS Date (midnight local time). */
export const parseYMD = (ymd) => {
  if (!ymd) return new Date();
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const addDays = (date, n) => {
  const next = new Date(date);
  next.setDate(next.getDate() + n);
  return next;
};

export const addMonths = (date, n) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + n);
  return next;
};

export const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** e.g. "13 Aug 2026" */
export const formatDisplayDate = (dateOrYMD) => {
  const date = typeof dateOrYMD === 'string' ? parseYMD(dateOrYMD) : dateOrYMD;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/** e.g. "August 2026" */
export const formatMonthLabel = (date) =>
  date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

/**
 * Turn an Axios error into a safe, user-facing message per the Operations
 * Dashboard error-handling spec. Never leaks raw backend/stack traces.
 * Returns null for 401 — the global Axios interceptor already redirects to
 * login in that case, so no in-page message is needed.
 */
export const getOperationsErrorMessage = (error) => {
  if (!error) return null;

  // No response at all -> network error (backend unreachable, CORS, offline, etc.)
  if (!error.response) {
    return 'Unable to connect to the server.';
  }

  const { status, data } = error.response;

  switch (status) {
    case 401:
      // Handled globally (token cleared + redirect to login).
      return null;
    case 403:
      return 'You do not have permission to access the Operations Dashboard.';
    case 404:
      return 'The requested camp was not found.';
    case 400:
      return data?.message || data?.error || data?.detail || 'Invalid request.';
    case 500:
      return 'Something went wrong. Please try again.';
    default:
      return data?.message || data?.error || data?.detail || 'Something went wrong. Please try again.';
  }
};

/** Safe accessor for possibly-missing numeric fields. */
export const num = (val, fallback = 0) =>
  typeof val === 'number' && !Number.isNaN(val) ? val : fallback;

/**
 * Format a numeric value as Indian Rupees for display, with safe handling
 * for null/undefined/empty values. Per the Financial Target UI spec, a
 * missing value must never render as "₹0" — callers should pass a
 * `fallback` that reflects what the missing value means in context
 * (e.g. "Not entered" for contract_value, "Not configured" for a target
 * amount Admin hasn't set yet).
 *
 *   formatINR(50000)                                     -> "₹50,000.00"
 *   formatINR('15000.00')                                -> "₹15,000.00"
 *   formatINR(null)                                      -> "Not entered"
 *   formatINR(undefined, { fallback: 'Not configured' }) -> "Not configured"
 */
export const formatINR = (value, { fallback = 'Not entered' } = {}) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return fallback;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(numeric);
};